import { apiFetch } from '../../lib/api/client.js';

export interface SpinPrize {
  key: string;
  label?: string;
  weight: number;
  cash?: number;
  /** Optional operator-uploaded artwork for the wedge; absent on older configs. */
  imageUrl?: string;
}

/**
 * Which requirement is holding a spin back. `unknown` is not a shortfall — the
 * server could not read the member's lifetime stats, so nobody knows yet.
 */
export type SpinGate =
  | 'minTotalDeposit'
  | 'minTotalTurnover'
  | 'minDepositCount'
  /**
   * The wheel's money bars are set in a currency this account does not hold, so
   * no figure of the member's is on the same scale as the bar. `required` is
   * then the CODE, not an amount.
   */
  | 'currency'
  | 'unknown';

export interface SpinEligibility {
  gate: SpinGate;
  /**
   * Digit string as the server stored it: minor units for the two money gates,
   * a plain count for `minDepositCount`. Null when nobody could tell.
   */
  required: string | null;
}

export interface SpinEvent {
  available: boolean;
  configId?: string;
  /** Minor units — cumulative deposit needed per spin. */
  depositPerSpin?: number;
  /** Bonus turnover requirement, e.g. 2 = 2x. */
  turnoverMultiplier?: number;
  maxPerDay?: number;
  balance?: number;
  /** Minor units — deposit carry accumulated toward the next spin. */
  depositAccum?: number;
  spinsRemainingToday?: number | null;
  /** Cap on entitlements GRANTED from deposits per Bangkok day. 0 = unlimited. */
  maxGrantsPerDay?: number;
  /**
   * How many entitlements today can still earn — `null` when the config sets no
   * grant cap, and ABSENT on a server older than #158. The two are not the same
   * thing to the reader, but both mean "say nothing", so the panel tests for a
   * number rather than for truthiness.
   */
  grantsRemainingToday?: number | null;
  prizes?: SpinPrize[];
  /**
   * Absent on servers older than #298, so only an explicit `false` blocks the
   * button — a missing field must never lock a player out of a working wheel.
   */
  eligible?: boolean;
  /** Present only while `eligible` is false. */
  eligibility?: SpinEligibility | null;
}

export interface SpinResult {
  id: string;
  prizeKey: string;
  prizeValue: SpinPrize;
  cashAwarded: number;
  createdAt: string;
  /**
   * Which wedge the server actually turned up, as a position in the prize table
   * it drew from. A key does not identify a wedge — a config stored before
   * svc-promo refused duplicates (PROMO-724) can carry the same key twice — so
   * this is what the pointer stops on. Absent on a server older than #555, and
   * absent from `/spin/history`, which never carried the table it was drawn
   * from; `landingIndex` falls back to the key in both cases.
   */
  prizeIndex?: number;
}

/**
 * A running event where the member still has a spin they can use today — the
 * condition every "spin waiting" nudge in the navigation lights up on.
 */
export function hasSpinWaiting(event: SpinEvent | undefined): boolean {
  return (
    !!event?.available &&
    (event.balance ?? 0) > 0 &&
    (event.spinsRemainingToday == null || event.spinsRemainingToday > 0)
  );
}

export const SPIN_QUERY_KEYS = {
  event: ['spin', 'event'] as const,
  history: ['spin', 'history'] as const,
};

export function fetchSpinEvent(): Promise<SpinEvent> {
  return apiFetch<SpinEvent>('/spin/event');
}

export function doSpin(): Promise<{ result: SpinResult; balance: number; turnoverRequired?: number }> {
  return apiFetch('/spin/draw', { method: 'POST' });
}

export function fetchSpinHistory(): Promise<{ items: SpinResult[]; total: number }> {
  return apiFetch('/spin/history');
}
